/**
 * SA-1077 (UX pass spec move 3, ruling Q1): which record-lane sections open by
 * default — only the section the viewer's seat owns at the current step: the
 * Feasibility section for the viewer who can open the feasibility wait, Quote
 * versions for the viewer who can open a quotes-surface wait. Everything else
 * closes; a remembered `record-section:<id>` state and a `?panel=` deep link
 * still win (`RecordSection`). The viewer's own open task is the Current task
 * block inside the Tasks section (SA-1034 PR 5), so `tasks` is owned whenever
 * `firstOpenable` holds; a wait owned by Feasibility or Quote versions opens
 * that section too. `ownedSections` is pure; `useOwnedSections` reads it over
 * the band's process read (one React Query key, never a second request).
 */
import { useMemo } from 'react'
import { useRecordProcess } from '@/hooks/useProcess'
import type { RecordProcessRead } from '@/types/process'
import { FEASIBILITY_SURFACE } from './feasibilityAwait'
import { firstOpenable, QUOTES_SURFACE } from './openTaskAction'

export type OwnedSection = 'tasks' | 'feasibility' | 'quotes'

export function ownedSections(record: RecordProcessRead | null | undefined): Set<OwnedSection> {
  const owned = new Set<OwnedSection>()
  const task = firstOpenable(record)
  if (!task) return owned
  owned.add('tasks')
  if (task.kind !== 'wait') return owned
  if (task.surface === FEASIBILITY_SURFACE) owned.add('feasibility')
  else if (task.surface === QUOTES_SURFACE) owned.add('quotes')
  return owned
}

export function useOwnedSections(orderId: string): Set<OwnedSection> {
  const record = useRecordProcess(orderId)
  const data = record.data ?? null
  return useMemo(() => ownedSections(data), [data])
}
